import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { MENU, TAKEAWAY_FEE, formatNaira } from "@/data/menu";
import { RESTAURANT } from "@/data/restaurant";
import { useCart } from "@/context/CartContext";

export default function MenuSection() {
  const [active, setActive] = useState(MENU[0].id);
  const current = MENU.find((m) => m.id === active);
  const { openDetails, openCart, totals } = useCart();

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-20 sm:py-28 bg-[#FFFCF5] border-y border-[#E8E2D6]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 reveal">
          <div>
            <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
              The Meritz Menu
            </p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
              Eat well. Stay long.
            </h2>
          </div>
          <button
            type="button"
            onClick={openCart}
            data-testid="menu-open-cart"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#344E41] hover:bg-[#2C2825] px-4 py-2.5 rounded-full transition-colors cursor-pointer"
          >
            <ShoppingBag size={14} />
            {totals.count > 0
              ? `View cart (${totals.count})`
              : "View cart"}
          </button>
        </div>

        <div
          data-testid="menu-alacarte-note"
          className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 reveal"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FCE9E1] text-[#C73E3A] text-xs font-semibold">
            À la carte — build your plate
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF3DC] text-[#8A5A00] text-xs font-semibold">
            🛵 {RESTAURANT.delivery.note}
          </span>
          <span className="text-xs sm:text-sm text-[#6B635A]">
            Take-away pack {formatNaira(TAKEAWAY_FEE)}.
          </span>
        </div>

        {/* Category tabs */}
        <div
          data-testid="menu-tabs"
          className="mt-8 flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2"
        >
          {MENU.map((m) => (
            <button
              key={m.id}
              onClick={() => setActive(m.id)}
              data-testid={`menu-tab-${m.id}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                active === m.id
                  ? "bg-[#2C2825] text-white border-[#2C2825]"
                  : "bg-white text-[#2C2825] border-[#E8E2D6] hover:border-[#2C2825]"
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div
          key={current.id}
          data-testid={`menu-panel-${current.id}`}
          className="mt-8 grid lg:grid-cols-5 gap-8 reveal in-view"
        >
          {current.image ? (
            <div className="lg:col-span-2">
              <div className="relative aspect-[4/5] sm:aspect-[3/4] rounded-3xl overflow-hidden border border-[#E8E2D6]">
                <img
                  src={current.image}
                  alt={current.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/75 to-transparent">
                  <p className="text-white/80 text-xs font-medium tracking-widest uppercase">
                    Category
                  </p>
                  <h3 className="font-display text-white text-3xl font-extrabold">
                    {current.name}
                  </h3>
                  <p className="text-white/90 text-sm mt-1 max-w-xs">
                    {current.blurb}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2">
              <div className="rounded-3xl border border-[#E8E2D6] bg-[#344E41] text-white p-8 h-full flex flex-col justify-end min-h-[280px]">
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase">
                  Category
                </p>
                <h3 className="font-display text-3xl font-extrabold mt-1">
                  {current.name}
                </h3>
                <p className="text-white/80 text-sm mt-2 max-w-xs">
                  {current.blurb}
                </p>
              </div>
            </div>
          )}

          <ul className="lg:col-span-3 divide-y divide-[#E8E2D6] bg-white rounded-3xl border border-[#E8E2D6] px-2 sm:px-3 py-2">
            {current.items.map((item) => (
              <li
                key={item.name}
                data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <button
                  type="button"
                  onClick={() => openDetails(item)}
                  className="w-full text-left py-5 px-3 sm:px-4 rounded-2xl hover:bg-[#F9F6F0] transition-colors group"
                >
                  <div className="menu-row">
                    <span className="font-display font-bold text-lg text-[#2C2825] flex items-center gap-2">
                      {item.name}
                      {item.promo && (
                        <span
                          data-testid="menu-item-promo-badge"
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#C73E3A] text-white text-[10px] font-bold tracking-wider uppercase"
                        >
                          Promo
                        </span>
                      )}
                    </span>
                    <span className="dots" />
                    <span className="font-semibold text-[#D9534F]">
                      {formatNaira(item.price)}
                    </span>
                  </div>
                  {item.desc && (
                    <p className="mt-1 text-sm text-[#2C2825]/65 max-w-md">
                      {item.desc}
                    </p>
                  )}
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-[#C73E3A] opacity-0 group-hover:opacity-100 transition-opacity">
                    <ShoppingBag size={11} /> Tap to add
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
